import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fetchMyRooms, verifyRoomPassword, fetchMessages,
  sendMessage, clearMessagesByOwner
} from '../services/api';

const s = {
  page: { minHeight: '100vh', background: '#080b10', display: 'flex', flexDirection: 'column' },
  nav: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 24px', borderBottom: '1px solid #1e2a36',
    background: '#0d1117', flexShrink: 0,
  },
  navLeft: { display: 'flex', alignItems: 'center', gap: '12px' },
  navBrand: {
    fontFamily: 'Syne, sans-serif', fontSize: '16px', fontWeight: '800', color: '#e2eaf3',
  },
  navBadge: {
    fontSize: '10px', fontFamily: 'JetBrains Mono, monospace',
    background: 'rgba(0,230,118,0.1)', color: '#00e676',
    border: '1px solid rgba(0,230,118,0.2)',
    borderRadius: '4px', padding: '2px 8px', letterSpacing: '0.1em',
  },
  navRight: { display: 'flex', gap: '12px', alignItems: 'center' },
  navInfo: { fontSize: '12px', color: '#7a90a4' },

  // Room picker
  pickerWrap: {
    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '40px',
  },
  pickerCard: {
    background: '#0d1117', border: '1px solid #1e2a36',
    borderRadius: '16px', padding: '32px', maxWidth: '500px', width: '100%',
  },
  pickerTitle: {
    fontFamily: 'Syne, sans-serif', fontSize: '20px', fontWeight: '700',
    color: '#e2eaf3', marginBottom: '20px',
  },
  roomItem: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 20px', background: '#131920',
    border: '1px solid #1e2a36', borderRadius: '10px',
    marginBottom: '12px', cursor: 'pointer', transition: 'border-color 0.2s',
  },
  roomItemName: { fontFamily: 'Syne, sans-serif', fontSize: '15px', fontWeight: '600', color: '#e2eaf3' },
  roomItemMeta: { fontSize: '11px', color: '#7a90a4', marginTop: '4px' },
  emptyText: { textAlign: 'center', color: '#3d5166', fontSize: '14px', padding: '40px 0' },

  // Chat layout
  chatWrap: { flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  chatHeader: {
    padding: '12px 24px', borderBottom: '1px solid #1e2a36',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    background: '#0d1117', flexShrink: 0,
  },
  chatRoomName: { fontFamily: 'Syne, sans-serif', fontSize: '16px', fontWeight: '700', color: '#e2eaf3' },
  chatHeaderRight: { display: 'flex', gap: '8px', alignItems: 'center' },

  messagesArea: {
    flex: 1, overflowY: 'auto', padding: '20px 24px',
    display: 'flex', flexDirection: 'column', gap: '12px',
  },
  msgBubble: {
    maxWidth: '70%', padding: '10px 16px',
    borderRadius: '14px', fontSize: '14px', lineHeight: '1.5',
    wordBreak: 'break-word',
  },
  msgSent: {
    background: 'rgba(0,230,118,0.1)', border: '1px solid rgba(0,230,118,0.15)',
    color: '#e2eaf3', alignSelf: 'flex-end', borderBottomRightRadius: '4px',
  },
  msgReceived: {
    background: '#131920', border: '1px solid #1e2a36',
    color: '#e2eaf3', alignSelf: 'flex-start', borderBottomLeftRadius: '4px',
  },
  msgSender: { fontSize: '11px', color: '#00e676', marginBottom: '4px', fontWeight: '600' },
  msgSenderOther: { fontSize: '11px', color: '#00e5ff', marginBottom: '4px', fontWeight: '600' },
  msgTime: { fontSize: '10px', color: '#3d5166', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px', justifyContent: 'flex-end' },
  tickRead: { color: '#00e5ff', fontSize: '12px' },
  tickSent: { color: '#7a90a4', fontSize: '12px' },
  msgSystem: {
    textAlign: 'center', fontSize: '12px', color: '#3d5166',
    padding: '8px', fontStyle: 'italic',
  },
  emptyChat: { textAlign: 'center', color: '#3d5166', fontSize: '14px', padding: '60px 0' },

  inputBar: {
    padding: '12px 24px', borderTop: '1px solid #1e2a36',
    background: '#0d1117', display: 'flex', gap: '12px',
    flexShrink: 0, position: 'relative',
  },
  inputField: {
    flex: 1, padding: '12px 16px', background: '#131920',
    border: '1px solid #1e2a36', borderRadius: '10px',
    color: '#e2eaf3', fontSize: '14px', outline: 'none',
    resize: 'none', maxHeight: '120px', overflowY: 'auto',
    fontFamily: 'inherit', lineHeight: '1.5',
  },
  emojiBtn: {
    background: 'none', border: 'none', fontSize: '22px',
    cursor: 'pointer', padding: '4px 8px', lineHeight: 1,
  },
  emojiPicker: {
    position: 'absolute', bottom: '60px', left: '24px',
    background: '#0d1117', border: '1px solid #1e2a36',
    borderRadius: '12px', padding: '12px',
    display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)',
    gap: '4px', maxWidth: '360px', maxHeight: '240px',
    overflowY: 'auto', zIndex: 300,
    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
  },
  emojiItem: {
    background: 'none', border: 'none', fontSize: '20px',
    cursor: 'pointer', padding: '4px', borderRadius: '6px',
    textAlign: 'center', lineHeight: 1,
  },
  sendBtn: {
    padding: '12px 24px', background: '#00e676', color: '#080b10',
    border: 'none', borderRadius: '10px', fontSize: '13px',
    fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap',
  },

  // Modal
  modal: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 200, backdropFilter: 'blur(4px)',
  },
  modalCard: {
    background: '#0d1117', border: '1px solid #1e2a36',
    borderRadius: '16px', padding: '32px', maxWidth: '400px', width: '90%',
  },
  modalTitle: { fontFamily: 'Syne, sans-serif', fontSize: '18px', fontWeight: '700', marginBottom: '12px', color: '#e2eaf3' },
  modalSub: { color: '#7a90a4', fontSize: '13px', lineHeight: '1.6', marginBottom: '24px' },
  modalBtns: { display: 'flex', gap: '12px', justifyContent: 'flex-end' },
  filterBtns: { display: 'flex', gap: '12px', marginBottom: '24px' },
  filterBtn: {
    flex: 1, padding: '12px', borderRadius: '8px',
    border: '1px solid #1e2a36', background: '#131920',
    color: '#7a90a4', fontSize: '13px', cursor: 'pointer',
  },
  filterBtnActive: {
    borderColor: '#00e676', color: '#00e676',
    background: 'rgba(0,230,118,0.05)',
  },
  toast: {
    position: 'fixed', bottom: '24px', right: '24px',
    background: '#131920', border: '1px solid #00e676',
    borderRadius: '10px', padding: '12px 20px',
    color: '#00e676', fontSize: '13px', zIndex: 1000,
  },
  loadingCenter: {
    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#3d5166', fontSize: '14px',
  },
};

function Toast({ message, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 2500); return () => clearTimeout(t); }, [onDone]);
  return <div style={s.toast}>✓ {message}</div>;
}

function ClearModal({ onConfirm, onCancel }) {
  const [filter, setFilter] = useState('all');
  return (
    <div style={s.modal} onClick={onCancel}>
      <div style={s.modalCard} onClick={e => e.stopPropagation()}>
        <div style={s.modalTitle}>Clear Messages</div>
        <div style={s.modalSub}>Choose what to clear from this room.</div>
        <div style={s.filterBtns}>
          <button
            style={filter === 'all' ? { ...s.filterBtn, ...s.filterBtnActive } : s.filterBtn}
            onClick={() => setFilter('all')}
          >
            Clear All<br /><small style={{ fontSize: '10px' }}>Read + Unread</small>
          </button>
          <button
            style={filter === 'read' ? { ...s.filterBtn, ...s.filterBtnActive } : s.filterBtn}
            onClick={() => setFilter('read')}
          >
            Clear Read Only<br /><small style={{ fontSize: '10px' }}>Keep Unread</small>
          </button>
        </div>
        <div style={s.modalBtns}>
          <button className="btn btn-ghost" onClick={onCancel}>Cancel</button>
          <button className="btn btn-danger" onClick={() => onConfirm(filter)}>Confirm</button>
        </div>
      </div>
    </div>
  );
}

function formatTime(sentAt) {
  try {
    const d = new Date(sentAt);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

const EMOJIS = [
  '😀','😃','😄','😁','😆','😅','😂','🤣',
  '😊','😇','🙂','😉','😍','🥰','😘','😎',
  '🤔','🤨','😐','😶','🙄','😏','😣','😥',
  '😮','🤐','😯','😪','😫','🥱','😴','😌',
  '😛','😜','🤪','😝','🤑','🤗','🤭','🤫',
  '🤐','🤥','😬','🤐','😯','😪','😫','🥱',
  '👍','👎','👌','✌️','🤞','🤟','🤘','👏',
  '🙌','🙏','🤝','💪','✊','👊','🤛','🤜',
  '❤️','🧡','💛','💚','💙','💜','🖤','🤍',
  '💔','❣️','💕','💞','💓','💗','💖','💘',
  '🔥','⭐','✨','💫','🌟','⚡','💥','💢',
  '🎉','🎊','🎁','🎈','🎂','🍰','🏆','🥇',
  '📞','📱','💻','⌨️','🖥️','🖨️','🖱️','💽',
  '💡','🔧','🔨','🛠️','⚙️','🧱','🏠','🏢',
  '🚗','🚕','🚙','✈️','🚀','🚲','🛵','🚶',
  '🐶','🐱','🐭','🐹','🐰','🦊','🐻','🐼',
  '🌸','🌺','🌻','🌹','🌷','🌳','🌲','🌴',
  '☕','🍵','🍺','🍻','🍷','🥃','🍸','🍹',
  '🍕','🍔','🍟','🌭','🍿','🥗','🍝','🍜',
  '⚽','🏀','🏈','⚾','🎾','🏐','🏉','🎱',
  '🎵','🎶','🎤','🎧','🎸','🎹','🥁','🎷',
];

function EmojiPicker({ onPick, onClose }) {
  return (
    <div style={s.emojiPicker} onClick={onClose}>
      {EMOJIS.map((emoji, i) => (
        <button
          key={i}
          style={s.emojiItem}
          onClick={(e) => { e.stopPropagation(); onPick(emoji); }}
        >
          {emoji}
        </button>
      ))}
    </div>
  );
}

export default function ChatConsole() {
  const navigate = useNavigate();
  const [token, setToken] = useState('');
  const [role, setRole] = useState('');
  const [username, setUsername] = useState('');
  const [loading, setLoading] = useState(true);
  const [rooms, setRooms] = useState([]);
  const [activeRoom, setActiveRoom] = useState(null); // { linkToken, password, name }
  const [roomToken, setRoomToken] = useState('');
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [sending, setSending] = useState(false);
  const [clearModal, setClearModal] = useState(false);
  const [toast, setToast] = useState('');
  const [showEmoji, setShowEmoji] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesAreaRef = useRef(null);
  const pollRef = useRef(null);

  // Load token + user info from localStorage
  useEffect(() => {
    const t = localStorage.getItem('userToken');
    const r = localStorage.getItem('userRole');
    const u = localStorage.getItem('username');
    if (!t) {
      navigate('/login');
      return;
    }
    setToken(t);
    setRole(r || 'USER');
    setUsername(u || 'user');
  }, [navigate]);

  // Fetch the user's rooms once the token is loaded
  const loadMyRooms = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await fetchMyRooms(token);
      setRooms(data);
      if (data.length === 1) {
        // Auto-join the single room
        joinRoom(data[0]);
      }
    } catch (e) {
      if (e.response?.status === 401) {
        handleLogout();
      }
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) loadMyRooms();
  }, [token, loadMyRooms]);

  // Join a room — verify password and get a room-scoped JWT
  const joinRoom = async (room) => {
    try {
      const senderName = username;
      const { data } = await verifyRoomPassword(room.linkToken, room.password, senderName);
      setActiveRoom({ linkToken: room.linkToken, password: room.password, name: data.roomName });
      setRoomToken(data.roomToken);
      loadMessages(room.linkToken, data.roomToken);
    } catch (e) {
      setToast('Failed to join room');
    }
  };

  // Load messages for the active room
  const loadMessages = async (linkToken, rToken) => {
    try {
      const { data } = await fetchMessages(linkToken, rToken);
      setMessages(data);
    } catch (e) {
      // silent
    }
  };

  // Poll for new messages every 3 seconds
  useEffect(() => {
    if (activeRoom && roomToken) {
      pollRef.current = setInterval(() => {
        loadMessages(activeRoom.linkToken, roomToken);
      }, 3000);
      return () => clearInterval(pollRef.current);
    }
  }, [activeRoom, roomToken]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesAreaRef.current) {
      messagesAreaRef.current.scrollTop = messagesAreaRef.current.scrollHeight;
    }
  }, [messages]);

  // Send a message
  const handleSend = async (e) => {
    e?.preventDefault();
    if (!inputText.trim() || !activeRoom) return;
    setSending(true);
    try {
      await sendMessage(activeRoom.linkToken, roomToken, inputText.trim(), username);
      setInputText('');
      setShowEmoji(false);
      await loadMessages(activeRoom.linkToken, roomToken);
    } catch (e) {
      setToast('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // Clear messages (room owner only)
  const handleClear = async (filter) => {
    try {
      await clearMessagesByOwner(activeRoom.linkToken, token, filter);
      await loadMessages(activeRoom.linkToken, roomToken);
      setToast(filter === 'read' ? 'Read messages cleared' : 'All messages cleared');
    } catch (e) {
      setToast('Failed to clear messages');
    }
    setClearModal(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('userToken');
    localStorage.removeItem('userRole');
    localStorage.removeItem('username');
    navigate('/login');
  };

  const switchRoom = () => {
    setActiveRoom(null);
    setRoomToken('');
    setMessages([]);
  };

  if (loading) {
    return (
      <div style={s.page}>
        <div style={s.loadingCenter}>Loading...</div>
      </div>
    );
  }

  // Room picker (shown when multiple rooms or no room joined yet)
  if (!activeRoom) {
    return (
      <div style={s.page}>
        <nav style={s.nav}>
          <div style={s.navLeft}>
            <span style={s.navBrand}>💬 SecretChat</span>
            <span style={s.navBadge}>{role}</span>
          </div>
          <div style={s.navRight}>
            <span style={s.navInfo}>{username}</span>
            <button className="btn btn-ghost" onClick={handleLogout} style={{ fontSize: '12px' }}>
              Sign Out
            </button>
          </div>
        </nav>

        <div style={s.pickerWrap}>
          <div style={s.pickerCard}>
            <h2 style={s.pickerTitle}>
              {rooms.length === 0 ? 'No Rooms Available' : 'Select a Room'}
            </h2>

            {rooms.length === 0 ? (
              <div style={s.emptyText}>
                You haven't been assigned to any room yet.
                <br />
                Please contact the admin.
              </div>
            ) : (
              rooms.map(room => (
                <div
                  key={room.id}
                  style={s.roomItem}
                  onClick={() => joinRoom(room)}
                  onMouseEnter={e => e.currentTarget.style.borderColor = '#00e676'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = '#1e2a36'}
                >
                  <div>
                    <div style={s.roomItemName}>{room.name}</div>
                    <div style={s.roomItemMeta}>
                      Code: {room.linkToken} · {room.messageCount} messages
                    </div>
                  </div>
                  <span style={{ color: '#00e676', fontSize: '18px' }}>→</span>
                </div>
              ))
            )}
          </div>
        </div>

        {toast && <Toast message={toast} onDone={() => setToast('')} />}
      </div>
    );
  }

  // Chat view
  return (
    <div style={s.page}>
      {/* Nav */}
      <nav style={s.nav}>
        <div style={s.navLeft}>
          <span style={s.navBrand}>💬 SecretChat</span>
          <span style={s.navBadge}>{role}</span>
        </div>
        <div style={s.navRight}>
          <span style={s.navInfo}>{username}</span>
          <button className="btn btn-ghost" onClick={handleLogout} style={{ fontSize: '12px' }}>
            Sign Out
          </button>
        </div>
      </nav>

      {/* Chat header */}
      <div style={s.chatHeader}>
        <div style={s.chatRoomName}>{activeRoom.name}</div>
        <div style={s.chatHeaderRight}>
          {rooms.length > 1 && (
            <button className="btn btn-ghost" onClick={switchRoom} style={{ fontSize: '11px' }}>
              ← Switch Room
            </button>
          )}
          {role === 'ROOM_OWNER' && (
            <button
              className="btn btn-ghost"
              onClick={() => setClearModal(true)}
              style={{ fontSize: '11px', color: '#ffb300', borderColor: '#ffb300' }}
            >
              🗑 Clear Chat
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div style={s.chatWrap}>
        <div style={s.messagesArea} ref={messagesAreaRef}>
          {messages.length === 0 ? (
            <div style={s.emptyChat}>No messages yet. Say hello! 👋</div>
          ) : (
            messages.map(msg => {
              if (msg.type === 'CLEAR') {
                return <div key={msg.id || msg.content} style={s.msgSystem}>{msg.content}</div>;
              }
              const isSent = msg.senderName === username;
              return (
                <div key={msg.id} style={{ ...s.msgBubble, ...(isSent ? s.msgSent : s.msgReceived) }}>
                  {!isSent && (
                    <div style={s.msgSenderOther}>{msg.senderName}</div>
                  )}
                  <div>{msg.content}</div>
                  <div style={s.msgTime}>
                    {formatTime(msg.sentAt)}
                    {isSent && (
                      <span style={msg.read ? s.tickRead : s.tickSent}>
                        {msg.read ? '✓✓' : '✓'}
                      </span>
                    )}
                  </div>
                </div>
              );
            })
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input bar */}
        <form style={s.inputBar} onSubmit={handleSend}>
          {showEmoji && (
            <EmojiPicker
              onPick={(emoji) => setInputText(prev => prev + emoji)}
              onClose={() => setShowEmoji(false)}
            />
          )}
          <button
            type="button"
            style={s.emojiBtn}
            onClick={() => setShowEmoji(prev => !prev)}
            title="Emoji"
          >
            😊
          </button>
          <textarea
            style={s.inputField}
            value={inputText}
            onChange={e => setInputText(e.target.value)}
            placeholder="Type a message..."
            maxLength={2000}
            disabled={sending}
            rows={1}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
          />
          <button type="submit" style={s.sendBtn} disabled={sending || !inputText.trim()}>
            {sending ? '...' : 'Send →'}
          </button>
        </form>
      </div>

      {/* Clear modal */}
      {clearModal && (
        <ClearModal
          onConfirm={handleClear}
          onCancel={() => setClearModal(false)}
        />
      )}

      {/* Toast */}
      {toast && <Toast message={toast} onDone={() => setToast('')} />}
    </div>
  );
}
